import { useState, useEffect } from "react";
import { Loader2, Sparkles } from "lucide-react";
import type { UseMutationResult } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import type { ProfileDetail, ProfilePayload, ApiResponse } from "@/interface";

interface SummarySectionProps {
  profile: ProfileDetail;
  patchProfile: UseMutationResult<
    ApiResponse<ProfileDetail>,
    Error,
    { id: number | string; payload: Partial<ProfilePayload> }
  >;
}

export function SummarySection({ profile, patchProfile }: SummarySectionProps) {
  const [summary, setSummary] = useState(profile?.summary || "");
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (profile) {
      setSummary(profile.summary || "");
    }
  }, [profile]);

  const generateAI = () => {
    setIsGenerating(true);
    setTimeout(() => {
      const aiSummary =
        "Highly accomplished professional with extensive expertise in designing scalable systems, driving project efficiency, and leading key initiatives. Recognized for strong analytical skills, comprehensive domain knowledge, and seamlessly delivering high-impact results.";
      setSummary(aiSummary);
      setIsGenerating(false);
    }, 1200);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.id) return;
    patchProfile.mutate({ id: profile.id, payload: { summary } });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm space-y-6"
    >
      <div className="flex justify-between items-center border-b border-slate-100 dark:border-slate-800 pb-4">
        <div>
          <h2 className="text-lg font-bold text-primary">
            Professional Summary
          </h2>
          <p className="text-xs text-slate-500">
            A brief overview of career and goals
          </p>
        </div>
        <Button
          type="button"
          onClick={generateAI}
          disabled={isGenerating}
          className="bg-[#1a0b2e] hover:bg-[#1a0b2e]/90 text-white text-xs h-8 min-w-[140px]"
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2 text-purple-400" />{" "}
              Auto-Generate
            </>
          )}
        </Button>
      </div>

      <div>
        <textarea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={8}
          className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-h-[160px]"
          placeholder="Write candidate professional summary..."
        />
      </div>

      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={patchProfile?.isPending}
          className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
        >
          {patchProfile?.isPending && (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          )}
          Save Changes
        </Button>
      </div>
    </form>
  );
}
